import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DubaiAreaSelect } from "@/components/DubaiAreaSelect";

export const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"];

export const emptyAddress = {
  emirate: "Dubai", property_type: "", building_name: "", unit_number: "", floor: "",
  villa_number: "", street: "", landmark: "", access_instructions: "", area: "",
};

export function isAddressComplete(a) {
  if (!a || !a.property_type || !a.area) return false;
  if (a.property_type === "apartment") return !!(a.building_name?.trim() && a.unit_number?.trim());
  if (a.property_type === "villa") return !!a.villa_number?.trim();
  return false;
}

export function buildCourierString(a) {
  if (!a || !a.property_type) return "";
  let head;
  if (a.property_type === "apartment") {
    head = [a.unit_number, a.floor, a.building_name].filter(Boolean).concat("Dubai").join(", ");
  } else {
    head = [a.villa_number ? `Villa ${a.villa_number}` : "", a.street].filter(Boolean).concat("Dubai").join(", ");
  }
  return [head, a.landmark, a.access_instructions].filter(Boolean).join(" · ");
}

// UAE mobile: accept 5X / 05X / +9715X / 009715X with spaces or dashes; normalise to "+971 5X XXX XXXX".
export function validateUaeMobile(m) {
  let s = String(m || "").replace(/[\s\-().]/g, "").replace(/^(\+971|00971|971)/, "");
  if (s.startsWith("0")) s = s.slice(1);
  if (!/^5\d{8}$/.test(s)) return { ok: false, normalized: "" };
  return { ok: true, normalized: `+971 ${s.slice(0, 2)} ${s.slice(2, 5)} ${s.slice(5)}` };
}
export const MOBILE_ERROR = "Please enter a UAE mobile number, e.g. 5X XXX XXXX";
export const localMobile = (m) => String(m || "").replace(/^\s*(\+971|00971)\s*/, "");

export function UaeMobileInput({ value, onChange, testid = "mobile-input" }) {
  return (
    <div className="flex items-stretch overflow-hidden rounded-lg border border-input focus-within:ring-2 focus-within:ring-ring">
      <span className="flex items-center bg-slate-100 px-3 text-sm font-medium text-slate-600" data-testid={`${testid}-prefix`}>+971</span>
      <input data-testid={testid} type="tel" inputMode="tel" value={localMobile(value)} onChange={(e) => onChange(e.target.value)}
        placeholder="5X XXX XXXX" className="h-10 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-slate-400" />
    </div>
  );
}

export default function AddressForm({ value, onChange }) {
  const a = value || emptyAddress;
  const set = (k) => (e) => onChange({ ...a, [k]: e.target?.value ?? e });
  const setType = (t) => onChange({
    ...a, property_type: t,
    ...(t === "apartment" ? { villa_number: "", street: "" } : { building_name: "", unit_number: "", floor: "" }),
  });

  return (
    <div className="space-y-4" data-testid="address-form">
      <div>
        <Label className="mb-1 block">Emirate<span className="text-red-500"> *</span></Label>
        <select data-testid="address-emirate" value="Dubai" disabled className="w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700">
          {EMIRATES.map((em) => <option key={em} value={em} disabled={em !== "Dubai"}>{em}</option>)}
        </select>
        <p className="mt-1 text-xs text-slate-400">We currently serve Dubai only — other emirates coming soon</p>
      </div>

      <div>
        <Label className="mb-1 block">Area<span className="text-red-500"> *</span></Label>
        <DubaiAreaSelect value={a.area || ""} onChange={(v) => onChange({ ...a, area: v })} testid="address-area" />
      </div>

      <div>
        <Label className="mb-1 block">Property type<span className="text-red-500"> *</span></Label>
        <div className="grid grid-cols-2 gap-2">
          {["apartment", "villa"].map((t) => (
            <button key={t} type="button" data-testid={`address-type-${t}`} onClick={() => setType(t)}
              className={`rounded-xl border px-3 py-2 text-sm font-medium capitalize transition-colors ${a.property_type === t ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {a.property_type === "apartment" && (
        <div className="space-y-3" data-testid="address-apartment-fields">
          <div><Label className="mb-1 block">Building name<span className="text-red-500"> *</span></Label><Input data-testid="address-building" value={a.building_name} onChange={set("building_name")} className="rounded-lg" /></div>
          <div><Label className="mb-1 block">Apartment number<span className="text-red-500"> *</span></Label><Input data-testid="address-unit" value={a.unit_number} onChange={set("unit_number")} className="rounded-lg" /></div>
          <div><Label className="mb-1 block">Floor</Label><Input data-testid="address-floor" value={a.floor} onChange={set("floor")} className="rounded-lg" /></div>
        </div>
      )}

      {a.property_type === "villa" && (
        <div className="space-y-3" data-testid="address-villa-fields">
          <div><Label className="mb-1 block">Villa number<span className="text-red-500"> *</span></Label><Input data-testid="address-villa" value={a.villa_number} onChange={set("villa_number")} className="rounded-lg" /></div>
          <div><Label className="mb-1 block">Street or cluster</Label><Input data-testid="address-street" value={a.street} onChange={set("street")} className="rounded-lg" /></div>
        </div>
      )}

      {!!a.property_type && (
        <>
          <div><Label className="mb-1 block">Nearest landmark</Label><Input data-testid="address-landmark" value={a.landmark} onChange={set("landmark")} placeholder="e.g. Opposite Spinneys, next to Union Metro" className="rounded-lg" /></div>
          <div>
            <Label className="mb-1 block">Access instructions</Label>
            <Textarea data-testid="address-access" value={a.access_instructions} onChange={set("access_instructions")} maxLength={200} placeholder="Gate code, security desk number, parking instructions" className="rounded-lg" />
            <p className="mt-1 text-right text-xs text-slate-400">{(a.access_instructions || "").length}/200</p>
          </div>
        </>
      )}
    </div>
  );
}
